import {
    addAudioLogEntry,
    errorMessageIsInteractBeforePlay,
    getPreferredAudioOutputId,
} from 'flow/utils/browser-utils';
import { setOutputDeviceWithIdToElement } from 'flow/utils/media-utils';
import knockerAlert from 'resources/audio/video-knocker-alert.mp3';
import participantJoinedAlert from 'resources/audio/video-participant-joined.mp3';

interface MeetingAlert {
    element: HTMLAudioElement;
    /**
     * The speaker the element is currently attached to, so it is only
     * re-attached when the choice actually changes. Attaching is an async
     * device switch, and doing one immediately before every play risks a late
     * or clipped start for no gain.
     *
     * Recorded without waiting to hear whether it worked, since the attach is
     * fire-and-forget. An attach that fails leaves the element on the output it
     * already had and is not retried for the same device — the alert still
     * plays, and picking a different speaker attaches again.
     */
    attachedAudioOutputId?: string;
}

/**
 * One element per sound, reused: these are short and the moments they mark are
 * rare, so there is nothing to gain from stacking players — and an element that
 * has already been allowed to play once will keep playing.
 *
 * Per sound rather than one shared player, so an arrival and a knock landing
 * together are both heard instead of the second cutting the first off. Keyed by
 * the alert's own name rather than by the file behind it, so that stays true
 * however the bundler names two files — and is what the caller means anyway.
 */
const alertByName = new Map<string, MeetingAlert>();

const getAlertElement = (name: string, source: string) => {
    let alert = alertByName.get(name);
    if (!alert) {
        const element = new Audio(source);
        element.preload = 'auto';
        alert = { element };
        alertByName.set(name, alert);
    }

    // Checked per play rather than attached once at creation: these elements
    // are not the meeting's audio reference, so changeAudioDevice never reaches
    // them, and someone who switches speaker mid-meeting should still be
    // alerted on the one they are listening to.
    const preferredAudioOutputId = getPreferredAudioOutputId();
    if (
        preferredAudioOutputId &&
        preferredAudioOutputId !== alert.attachedAudioOutputId
    ) {
        setOutputDeviceWithIdToElement(preferredAudioOutputId, alert.element);
        alert.attachedAudioOutputId = preferredAudioOutputId;
    }

    return alert.element;
};

/**
 * Best-effort: an alert that cannot be played is logged and dropped, never
 * thrown — what it announces is visible on screen either way, and a meeting
 * must not break over a sound.
 *
 * Browsers refuse audio until the page has been interacted with. Joining a
 * meeting is itself an interaction, so in practice this only bites when a
 * meeting is restored into a tab nobody has touched yet.
 */
const playAlert = (name: string, source: string) => {
    try {
        const element = getAlertElement(name, source);
        // Rewound rather than left to finish, so two of these close together
        // are two audible alerts instead of the second being swallowed.
        element.currentTime = 0;
        const playPromise = element.play();
        playPromise?.catch((error: Error) => {
            if (errorMessageIsInteractBeforePlay(error)) {
                addAudioLogEntry(
                    `${name}: no interaction yet, dropping the alert. ${error}`,
                );
                return;
            }
            addAudioLogEntry(`${name}: failed. ${error}`);
        });
    } catch (error) {
        addAudioLogEntry(`${name}: failed. ${error}`);
    }
};

/** Announces that someone is now waiting to be let in (VID-76). */
export const playWaitingParticipantAlert = () =>
    playAlert('playWaitingParticipantAlert', knockerAlert);

/**
 * Announces that someone has joined the meeting — including a guest who was
 * just admitted, since being let in is how they join.
 *
 * Not the same event as a knock: this is someone arriving in the room, and
 * everyone already in it hears it. The person joining does not, since the
 * server does not announce a participant to themselves.
 */
export const playParticipantJoinedAlert = () =>
    playAlert('playParticipantJoinedAlert', participantJoinedAlert);
